<template>
  <div class="m-auto m-1230">
    <div class="row align-items-center">
      <div class="col-lg-6 d-none d-lg-block">
        <img src="@/assets/images/login.jpg" class="rounded-3" alt="reset password" />
      </div>
      <div class="col-lg-6">
        <div class="mw-480 ms-lg-auto">
          <h3 class="fs-28 mb-2">Imposta Nuova Password</h3>
          <p class="fw-medium fs-16 mb-4">
            Inserisci la tua nuova password per completare il reset.
          </p>

          <form @submit.prevent="handleResetPassword" class="auth-form">
            <!-- Success Message -->
            <div v-if="successMessage" class="alert alert-success d-flex align-items-center" role="alert">
              <i class="material-symbols-outlined me-2">check_circle</i>
              <span>{{ successMessage }}</span>
            </div>

            <!-- API Error Message -->
            <div v-if="apiError" class="alert alert-danger d-flex align-items-center" role="alert">
              <i class="material-symbols-outlined me-2">error</i>
              <span>{{ apiError }}</span>
            </div>

            <!-- Token/Email validation error -->
            <div v-if="tokenError" class="alert alert-warning d-flex align-items-center" role="alert">
              <i class="material-symbols-outlined me-2">warning</i>
              <span>{{ tokenError }}</span>
            </div>

            <div v-if="!resetCompleted">
              <div class="form-floating mb-4">
                <input
                  type="email"
                  class="form-control h-55"
                  id="resetEmail"
                  placeholder="esempio@email.com"
                  v-model="email"
                  disabled
                />
                <label for="resetEmail">Indirizzo Email</label>
              </div>

              <div class="form-floating mb-4 position-relative">
                <input
                  :type="passwordFieldType"
                  class="form-control h-55"
                  :class="{ 'is-invalid': passwordError }"
                  id="newPassword"
                  placeholder="Nuova password"
                  v-model="password"
                  @blur="validatePassword"
                  @input="validatePassword"
                  required
                />
                <label for="newPassword">Nuova Password</label>
                <button
                  type="button"
                  class="btn border-0 position-absolute top-50 end-0 translate-middle-y d-flex align-items-center justify-content-center"
                  @click="togglePasswordVisibility"
                >
                  <i class="material-symbols-outlined">
                    {{ isPasswordVisible ? 'visibility_off' : 'visibility' }}
                  </i>
                </button>
                <div v-if="passwordError" class="invalid-feedback">
                  {{ passwordError }}
                </div>
              </div>

              <div class="form-floating mb-4 position-relative">
                <input
                  :type="confirmPasswordFieldType"
                  class="form-control h-55"
                  :class="{ 'is-invalid': confirmPasswordError }"
                  id="confirmPassword"
                  placeholder="Conferma password"
                  v-model="passwordConfirmation"
                  @blur="validatePasswordConfirmation"
                  @input="validatePasswordConfirmation"
                  required
                />
                <label for="confirmPassword">Conferma Password</label>
                <button
                  type="button"
                  class="btn border-0 position-absolute top-50 end-0 translate-middle-y d-flex align-items-center justify-content-center"
                  @click="toggleConfirmPasswordVisibility"
                >
                  <i class="material-symbols-outlined">
                    {{ isConfirmPasswordVisible ? 'visibility_off' : 'visibility' }}
                  </i>
                </button>
                <div v-if="confirmPasswordError" class="invalid-feedback">
                  {{ confirmPasswordError }}
                </div>
              </div>

              <div class="form-group mb-4">
                <button
                  type="submit"
                  class="btn btn-primary fw-medium py-2 px-3 w-100"
                  :disabled="!isFormValid || isLoading"
                >
                  <div v-if="isLoading" class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <div v-else class="d-flex align-items-center justify-content-center py-1">
                    <i class="material-symbols-outlined text-white fs-20 me-2">lock_reset</i>
                    <span>Cambia Password</span>
                  </div>
                </button>
              </div>
            </div>

            <div v-else class="text-center">
              <div class="mb-4">
                <i class="material-symbols-outlined text-success" style="font-size: 4rem;">check_circle</i>
              </div>
              <p class="mb-4">
                Password cambiata con successo! Ora puoi effettuare il login con la nuova password.
              </p>
              <RouterLink
                to="/authentication/login"
                class="btn btn-primary fw-medium py-2 px-3"
              >
                Vai al Login
              </RouterLink>
            </div>

            <div class="form-group" v-if="!resetCompleted">
              <p class="text-center">
                Ti sei ricordato la password?
                <RouterLink
                  to="/authentication/login"
                  class="fw-medium text-primary text-decoration-none"
                >
                  Torna al Login
                </RouterLink>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import authService from "@/services/authService";
import { formatErrorMessage } from "@/utils/errorUtils";

export default defineComponent({
  name: "ResetPassword",
  setup() {
    const route = useRoute();
    const router = useRouter();

    const token = ref('');
    const email = ref('');
    const password = ref('');
    const passwordConfirmation = ref('');
    const passwordError = ref('');
    const confirmPasswordError = ref('');
    const apiError = ref('');
    const successMessage = ref('');
    const tokenError = ref('');
    const isLoading = ref(false);
    const resetCompleted = ref(false);

    const isPasswordVisible = ref(false);
    const isConfirmPasswordVisible = ref(false);
    const passwordFieldType = ref('password');
    const confirmPasswordFieldType = ref('password');

    const togglePasswordVisibility = () => {
      isPasswordVisible.value = !isPasswordVisible.value;
      passwordFieldType.value = isPasswordVisible.value ? 'text' : 'password';
    };

    const toggleConfirmPasswordVisibility = () => {
      isConfirmPasswordVisible.value = !isConfirmPasswordVisible.value;
      confirmPasswordFieldType.value = isConfirmPasswordVisible.value ? 'text' : 'password';
    };

    const validatePassword = () => {
      if (!password.value) {
        passwordError.value = 'La password è obbligatoria';
        return false;
      }
      if (password.value.length < 8) {
        passwordError.value = 'La password deve essere di almeno 8 caratteri';
        return false;
      }
      passwordError.value = '';
      return true;
    };

    const validatePasswordConfirmation = () => {
      if (!passwordConfirmation.value) {
        confirmPasswordError.value = 'La conferma password è obbligatoria';
        return false;
      }
      if (password.value !== passwordConfirmation.value) {
        confirmPasswordError.value = 'Le password non coincidono';
        return false;
      }
      confirmPasswordError.value = '';
      return true;
    };

    const isFormValid = computed(() => {
      return password.value &&
             passwordConfirmation.value &&
             !passwordError.value &&
             !confirmPasswordError.value &&
             token.value &&
             email.value;
    });

    const handleResetPassword = async () => {
      if (!validatePassword() || !validatePasswordConfirmation()) {
        return;
      }

      isLoading.value = true;
      apiError.value = '';
      successMessage.value = '';

      try {
        const response = await authService.resetPassword({
          token: token.value,
          email: email.value,
          password: password.value,
          password_confirmation: passwordConfirmation.value
        });

        console.log('ResetPassword Response:', response);
        console.log('Response data:', response.data);
        console.log('Response status:', response.status);

        // Check for success - Laravel might return different response structures
        if (response.status === 200 || response.data.status === 200) {
          successMessage.value = response.data.message || 'Password reimpostata con successo! Ora puoi effettuare il login con la nuova password.';
          resetCompleted.value = true;
        }
      } catch (error: any) {
        console.error("Reset password failed:", error);
        apiError.value = formatErrorMessage(error);
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      // Extract token and email from URL parameters
      token.value = (route.query.token as string) || '';
      email.value = (route.query.email as string) || '';

      // Validate required parameters
      if (!token.value || !email.value) {
        tokenError.value = 'Link di reset non valido. Token o email mancanti.';
      }
    });

    return {
      token,
      email,
      password,
      passwordConfirmation,
      passwordError,
      confirmPasswordError,
      apiError,
      successMessage,
      tokenError,
      isLoading,
      resetCompleted,
      isPasswordVisible,
      isConfirmPasswordVisible,
      passwordFieldType,
      confirmPasswordFieldType,
      togglePasswordVisibility,
      toggleConfirmPasswordVisibility,
      isFormValid,
      validatePassword,
      validatePasswordConfirmation,
      handleResetPassword
    };
  },
});
</script>